import React, { FC } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Flex, Text } from '@amzn/storm-ui';
import CombineAccountsLayout from '@features/combine-accounts/components/CombineAccountsLayout';
import CombineAccountsSteps, {
  CombineAccountsStep,
} from '@features/combine-accounts/components/CombineAccountsSteps';
import CombineAccountsActionButtons from '@features/combine-accounts/components/CombineAccountsActionButtons';
import {
  COMBINE_ACCOUNTS_LANDING_PATH,
  COMBINE_ACCOUNTS_DETAILS_PATH,
  COMBINE_ACCOUNTS_REVIEW_PATH,
} from 'src/shared/routes';
import {
  COMBINE_ACCOUNTS_NEXT_KEY,
  GA_PARAM,
  CFID_PARAM,
} from '@features/combine-accounts/constants';
import useIntl from '@i18n/useIntl';
import { useGetBillingDetailsQuery } from 'src/shared/services/combine-accounts/combineAccounts';
import BillingDetailsCard from './components/BillingDetailsCard';
import {
  COMBINE_BILLING_LOADING_KEY,
  COMBINE_BILLING_ERROR_KEY,
  COMBINE_BILLING_MISSING_PARAMS_KEY,
} from './constants';

const PAGE_ID = 'combine-accounts-billing-page';

/**
 * Billing Details page (Step 2) in the Combine Accounts flow.
 *
 * Displays read-only billing profile information for both the SA (Sponsored ads)
 * and DSP (Amazon DSP) accounts being combined.
 *
 * Data source: the ARMS BFF billing-details endpoint, which reads each side's
 * bill-to billing profile via AdvertisingBillingApiService listBillingProfiles.
 * The DSP (Manager Account) side is resolved server-side from the authenticated
 * context. Either side can be null when its billing data is unavailable: the
 * card hides the SA tab, while the DSP tab always renders and falls back to
 * showing only the invoicing type (DSP accounts in this flow are always
 * invoiced to the Manager Account).
 */
const CombineAccountsBillingPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { intl } = useIntl();

  const globalAccountId = searchParams.get(GA_PARAM);
  const cfid = searchParams.get(CFID_PARAM);

  const { data, isLoading, isError } = useGetBillingDetailsQuery(
    { globalAccountId: globalAccountId ?? '', cfid: cfid ?? '' },
    { skip: !globalAccountId || !cfid },
  );

  const handleCancel = () => navigate(COMBINE_ACCOUNTS_LANDING_PATH);
  const handleGoBack = () =>
    navigate(`${COMBINE_ACCOUNTS_DETAILS_PATH}?${searchParams.toString()}`);
  const handleNext = () => {
    const params = new URLSearchParams(searchParams);
    navigate(`${COMBINE_ACCOUNTS_REVIEW_PATH}?${params.toString()}`);
  };
  const handleClose = () => navigate(COMBINE_ACCOUNTS_LANDING_PATH);

  const actionButtons = (
    <CombineAccountsActionButtons
      onCancel={handleCancel}
      onGoBack={handleGoBack}
      onSubmit={handleNext}
      submitLabelKey={COMBINE_ACCOUNTS_NEXT_KEY}
    />
  );

  if (!globalAccountId || !cfid) {
    return (
      <Box id={PAGE_ID} data-testid={PAGE_ID}>
        <CombineAccountsLayout onClose={handleClose}>
          <Text color="secondary">
            {intl.getMessage(COMBINE_BILLING_MISSING_PARAMS_KEY)}
          </Text>
        </CombineAccountsLayout>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box id={PAGE_ID} data-testid={PAGE_ID}>
        <CombineAccountsLayout onClose={handleClose}>
          <Text>{intl.getMessage(COMBINE_BILLING_LOADING_KEY)}</Text>
        </CombineAccountsLayout>
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Box id={PAGE_ID} data-testid={PAGE_ID}>
        <CombineAccountsLayout onClose={handleClose}>
          <Text color="secondary">
            {intl.getMessage(COMBINE_BILLING_ERROR_KEY)}
          </Text>
        </CombineAccountsLayout>
      </Box>
    );
  }

  return (
    <Box id={PAGE_ID} data-testid={PAGE_ID}>
      <CombineAccountsLayout
        actionButtons={actionButtons}
        onClose={handleClose}
      >
        <Box paddingBlockEnd="small">
          <CombineAccountsSteps current={CombineAccountsStep.BillingDetails} />
        </Box>

        <Flex direction="column" rowGap="small">
          <BillingDetailsCard
            sponsoredAdsBilling={data.sponsoredAdsBilling}
            amazonDspBilling={data.amazonDspBilling}
          />
        </Flex>

        <Flex justifyContent="flex-end" paddingBlockStart="small">
          {actionButtons}
        </Flex>
      </CombineAccountsLayout>
    </Box>
  );
};

export default CombineAccountsBillingPage;
